import React, { useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { ResponseSubscriptionType } from '@r1-frontend/api-domru/api-tariffs/v1/tariffs/[salePackageId]/subscriptions';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import { ListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { TvSubscription } from '@r1-frontend/ui-react/organisms/TariffSubscription';
import { getPluralize } from '@r1-frontend/shared/helpers/pluralize';

import { useAnalytics } from '~/src/features/full-buy/analytics/context';
import SubscriptionsAdditionalInfoPopup from '~/src/features/full-buy/components/SubscriptionsPopup/SubscriptionsAdditionalInfoPopup';
import { selectSubscriptionPopup } from '~/src/features/full-buy/helpers/selectSubscriptionPopup';
import { selectSubscription, unSelectSubscription } from '~/src/features/full-buy/store/actions';
import {
    getRequiredAndSelectivelySubscriptions,
} from '~/src/features/full-buy/store/selectors/getRequiredAndSelectivelySubscriptions';

const subscriptionPlural = getPluralize(['дополнительную подписку', 'дополнительные подписки', 'дополнительных подписок']);

const initPopupData = {
    displayImage: '',
    title: '',
    description: [],
    isOpen: false,
};
type TSubscriptions = {
    displayImage: string,
    title: string,
    description: string[],
    isOpen: boolean,
}

const RequiredAndSelectivelySubscriptionsContainer = ({ className }: { className?: string }): JSX.Element => {
    const analytics = useAnalytics();

    const dispatch = useDispatch();
    const { subscriptions, selected, leftToSelect, needToSelect } = useSelector(getRequiredAndSelectivelySubscriptions);
    const [selectedSubscription, setState] = useState<TSubscriptions>(initPopupData);
    const handleToggleMultiple = (id: number, title: string) => () => {
        const state = !selected.includes(id);
        const callback = state
            ? selectSubscription
            : unSelectSubscription;

        dispatch(callback(id, title, analytics.subscriptions));
    };

    const handleToggleOneOf = (id: number, title: string) => () => {
        const state = !selected.includes(id);
        const callback = state
            ? selectSubscription
            : unSelectSubscription;

        const listOfActions = [callback(id, title, analytics.subscriptions)];

        if (state) {
            subscriptions.oneOf
                .filter(({ serviceId }) => serviceId !== id)
                .forEach(({ serviceId }) => {
                    listOfActions.push(unSelectSubscription(serviceId));
                });
        }

        batch(() => {
            listOfActions.forEach(dispatch);
        });
    };

    const handleIconClick = (id: number, type: ResponseSubscriptionType) => () => {
        const subscription = subscriptions[type].find(s => s.serviceId === id);
        if (subscription) {
            setState(subscription ? selectSubscriptionPopup(subscription) : initPopupData);
        }
    };

    const handleClosePopup = () => {
        setState(initPopupData);
    };

    return <div className={className}>
        <H3>Выберите одну из основных подписок</H3>
        <ListContainer $mobile={8} $desktop={16}>
            {subscriptions.oneOf.map(subscription => (
                <TvSubscription
                    key={`FullBuy-Subscription-${subscription.serviceId}`}
                    onToggle={handleToggleOneOf(subscription.serviceId, subscription.title)}
                    imagePath={subscription.images}
                    onIconClick={handleIconClick(subscription.serviceId, ResponseSubscriptionType.oneOf)}
                    description={subscription.description}
                    checked={selected.includes(subscription.serviceId)}
                >
                    {subscription.title}
                </TvSubscription>
            ))}
        </ListContainer>

        <H3>Выберите {needToSelect.multiple > 1 ? `${needToSelect.multiple} ` : ''}{subscriptionPlural(needToSelect.multiple)}</H3>
        <ListContainer $mobile={8} $desktop={16}>
            {subscriptions.multiple.map(subscription => (
                <TvSubscription
                    key={`FullBuy-Subscription-${subscription.serviceId}`}
                    onToggle={handleToggleMultiple(subscription.serviceId, subscription.title)}
                    imagePath={subscription.images}
                    onIconClick={handleIconClick(subscription.serviceId, ResponseSubscriptionType.multiple)}
                    description={subscription.description}
                    disabled={!leftToSelect.multiple && !selected.includes(subscription.serviceId)}
                    checked={selected.includes(subscription.serviceId)}
                >
                    {subscription.title}
                </TvSubscription>
            ))}
        </ListContainer>
         <SubscriptionsAdditionalInfoPopup
             onClose={handleClosePopup}
             {...selectedSubscription}
         />
    </div>;
};

export default styled(RequiredAndSelectivelySubscriptionsContainer)`
    ${ListContainer}:not(:last-child) {
        margin-bottom: 24px;
    }

    ${ListContainer} {
        width: 100%;
        flex-wrap: nowrap;
        flex-direction: column;
    }

    @media (min-width: ${wideBreakpoints.mobile}) {
        ${ListContainer}:not(:last-child) {
            margin-bottom: 32px;
        }

        ${ListContainer} {
            flex-direction: row;
        }

        ${TvSubscription} {
            min-height: 100%;
            width: 275px;
        }
        
        ${TvSubscription} img {
            height: auto;
        }
    }
`;
